import { useState, useEffect, useCallback, useRef } from 'react'
import axios from 'axios'
import moment from 'moment'
import { prop, toUpper, contains, compose, isEmpty, isNil, not, anyPass, concat } from 'ramda'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'
import { Store } from '../context/store'
import { apiUrl } from '../config'


const isReadRequest = method => contains(toUpper(method), ['GET', 'HEAD', 'OPTIONS'])
const setConfig = (token, config) => {
    const isSetToken = compose(not, anyPass([isEmpty, isNil]))
    if (isSetToken(token)) {
        return {
            ...config,
            headers: {
                Authorization: `Bearer ${token}`
            }
        }
    }
    return config
}

const useFetch = ({
    url = '',
    method = 'GET',
    params = {},
    lazy = null,
    action,
    state: manageState = true,
    loader = true,
    returnLastUpdate = false,
    reload = true
}) => {
    const { state: { app: { online }, user: { token, refresh: isUserEnabledRefresh }, timestamp }, actions: { setLoginFailure } } = Store()
    const [state, setState] = useState({
        response: {
            data: [],
            lastUpdate: null
        },
        error: null,
        fetching: false
    })

    const { t } = useTranslation()
    const refSource = useRef(null)

    // Default GET|HEAD|OPTIONS requests to non-lazy (automatically request onMount)
    // all else lazy (only requested when doFetch is called)
    const isLazy = lazy === null ? !isReadRequest(method) : lazy

    const doFetch = useCallback(
        (additionalParams = {}) => new Promise(async (resolve, reject) => {
            if (refSource.current) {
                refSource.current.cancel("Previous request canceled.")
            }
            refSource.current = axios.CancelToken.source()
            if (online) {
                const { data = {}, loader: _loader, url: _url, returnLastUpdate: _returnLastUpdate } = additionalParams
                const isLoader = _loader === undefined ? loader : _loader
                const isReturnLastUpdate = _returnLastUpdate === undefined ? returnLastUpdate : _returnLastUpdate
                if (manageState && isLoader) {
                    setState(prevState => ({
                        ...prevState,
                        fetching: true
                    }))
                }
                try {
                    const res = await axios(setConfig(token, {
                        url: concat(apiUrl, _url || url),
                        method: toUpper(method),
                        params,
                        data,
                        responseType: 'json',
                        cancelToken: refSource.current.token
                    }))

                    const resData = {
                        data: prop('data', res),
                        lastUpdate: moment().unix()
                    }
                    action && action(resData)
                    manageState && setState(prevState => ({
                        ...prevState,
                        response: resData,
                        fetching: false
                    }))
                    if (isReturnLastUpdate) {
                        resolve(resData);
                    } else {
                        resolve(prop('data', res));
                    }
                } catch (err) {
                    if (axios.isCancel(err)) {
                        console.log('Request canceled.', prop('message', err));
                    } else if ((err && err.response) && (err.response.status === 401 || err.response.status === 403)) {
                        //401 -> Unauthorized
                        //403 -> Forbidden
                        setLoginFailure()
                    } else {
                        toast.error(t('error_fetch'))
                        manageState && setState(prevState => ({
                            ...prevState,
                            error: prop('response', err),
                            fetching: false
                        }))
                    }
                    reject(err)
                }
            }
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [online, url]
    )

    const firstUpdate = useRef(true);
    useEffect(() => {
        if (firstUpdate.current) {
            firstUpdate.current = false;
            return;
        }
        if (!!isUserEnabledRefresh && reload && timestamp !== 0 && toUpper(method) === "GET") {
            doFetch({ loader: false })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [timestamp])

    // Start requesting onMount if not lazy
    // Start requesting if isLazy goes from true to false
    // Start requesting every time the request key changes (i.e. URL, method, init.body or init.responseType) if not lazy
    useEffect(() => {
        // Do not start request automatically when in lazy mode
        if (isLazy || !manageState) {
            return
        }

        doFetch()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLazy, url, manageState])

    // Cancel any running request when unmounting to avoid updating state after component has unmounted
    // This can happen if a request's promise resolves after component unmounts
    useEffect(() => {
        return () => {
            refSource.current && refSource.current.cancel('Component unmounted.')
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    if (manageState) {
        return {
            response: prop('response', state),
            error: prop('error', state),
            fetching: prop('fetching', state),
            doFetch
        }
    } else {
        return {
            doFetch
        }
    }
}

export default useFetch